// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { Card, Checkbox, Divider, Typography } from '@mui/material';
import { Controller } from 'react-hook-form';
import {
  Input,
  Label,
  OptionsSelector,
  DateInput,
} from '@shared/ui-kit/components';
import { Translation } from '@shared/ui-kit/providers';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import {
  NavigationControls,
  ScreenLayout,
} from '@shared/advisory/account-workflow/components/account-opening/onboarding';
import { usePersonalData } from './use-personal-data';

export const PersonalData = defineComponent({
  displayName: 'AccountOpeningPersonalData',
  useSetup: usePersonalData,
  View: ({
    form,
    onNext,
    isFormValid,
    countryOptions,
    maritalStatusOptions,
    genderOptions,
    documentTypeOptions,
    postalProvinceOptions,
    fiscalProvinceOptions,
    streetTypeOptions,
    hasDifferentFiscalAddress,
    hasPermanentValidity,
    hasPostalProvinceOptions,
    hasFiscalProvinceOptions,
    minDocumentExpirationDate,
    t,
    isUpdatingProfile,
  }) => {
    return (
      <ScreenLayout
        actions={
          <NavigationControls
            form="personal-data-form"
            isFormValid={isFormValid}
            loading={isUpdatingProfile}
          />
        }
      >
        <div className="w-full">
          <form
            id="personal-data-form"
            onSubmit={form.handleSubmit(onNext)}
            aria-busy={isUpdatingProfile || undefined}
          >
            <fieldset
              disabled={isUpdatingProfile}
              className="m-0 flex min-w-0 flex-col gap-6 border-0 p-0"
            >
              <Card
                className="!flex !flex-col !gap-2 !rounded-lg !p-6"
                variant="outlined"
              >
                <Typography
                  variant="h6"
                  className="!mb-2 !font-semibold"
                  data-testid="basic-info-section-title"
                >
                  <Translation
                    i18nKey="account-opening.personal-data.basic-info.title"
                    ns="components"
                  />
                </Typography>

                <Typography
                  variant="body2"
                  className="!mb-6 !text-gray-500"
                  data-testid="basic-info-section-subtitle"
                >
                  <Translation
                    i18nKey="account-opening.personal-data.basic-info.subtitle"
                    ns="components"
                  />
                </Typography>

                <div className="flex flex-col gap-4">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-12">
                    <fieldset className="col-span-1 grid gap-2 lg:col-span-6">
                      <Label htmlFor="first-name-input" required>
                        <Translation
                          i18nKey="account-opening.personal-data.basic-info.first-name"
                          ns="components"
                        />
                      </Label>
                      <Controller
                        control={form.control}
                        name="first_name"
                        render={({ field, fieldState }) => (
                          <Input
                            id="first-name-input"
                            {...field}
                            placeholder={t(
                              'account-opening.personal-data.basic-info.first-name-placeholder',
                              {
                                ns: 'components',
                              },
                            )}
                            data-testid="first-name-input"
                            required
                            error={!!fieldState.error}
                          />
                        )}
                      />
                    </fieldset>

                    <fieldset className="col-span-1 grid gap-2 lg:col-span-6">
                      <Label htmlFor="second-name-input">
                        <Translation
                          i18nKey="account-opening.personal-data.basic-info.second-name"
                          ns="components"
                        />
                      </Label>
                      <Controller
                        control={form.control}
                        name="second_name"
                        render={({ field, fieldState }) => (
                          <Input
                            id="second-name-input"
                            {...field}
                            placeholder={t(
                              'account-opening.personal-data.basic-info.second-name-placeholder',
                              {
                                ns: 'components',
                              },
                            )}
                            data-testid="second-name-input"
                            error={!!fieldState.error}
                          />
                        )}
                      />
                    </fieldset>
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-12">
                    <fieldset className="col-span-1 grid gap-2 lg:col-span-6">
                      <Label htmlFor="first-surname-input" required>
                        <Translation
                          i18nKey="account-opening.personal-data.basic-info.first-surname"
                          ns="components"
                        />
                      </Label>
                      <Controller
                        control={form.control}
                        name="first_surname"
                        render={({ field, fieldState }) => (
                          <Input
                            id="first-surname-input"
                            {...field}
                            placeholder={t(
                              'account-opening.personal-data.basic-info.first-surname-placeholder',
                              {
                                ns: 'components',
                              },
                            )}
                            data-testid="first-surname-input"
                            required
                            error={!!fieldState.error}
                          />
                        )}
                      />
                    </fieldset>

                    <fieldset className="col-span-1 grid gap-2 lg:col-span-6">
                      <Label htmlFor="second-surname-input">
                        <Translation
                          i18nKey="account-opening.personal-data.basic-info.second-surname"
                          ns="components"
                        />
                      </Label>
                      <Controller
                        control={form.control}
                        name="second_surname"
                        render={({ field, fieldState }) => (
                          <Input
                            id="second-surname-input"
                            {...field}
                            placeholder={t(
                              'account-opening.personal-data.basic-info.second-surname-placeholder',
                              {
                                ns: 'components',
                              },
                            )}
                            data-testid="second-surname-input"
                            error={!!fieldState.error}
                          />
                        )}
                      />
                    </fieldset>
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <fieldset className="col-span-1 grid gap-2">
                      <Label htmlFor="birth-date-input" required>
                        <Translation
                          i18nKey="account-opening.personal-data.basic-info.birth-date"
                          ns="components"
                        />
                      </Label>
                      <Controller
                        control={form.control}
                        name="birth_date"
                        render={({ field, fieldState }) => (
                          <DateInput
                            id="birth-date-input"
                            name={field.name}
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            ref={field.ref}
                            data-testid="birth-date-input"
                            required
                            error={!!fieldState.error}
                          />
                        )}
                      />
                    </fieldset>

                    <fieldset className="col-span-1 grid gap-2">
                      <Label htmlFor="birth-city-input" required>
                        <Translation
                          i18nKey="account-opening.personal-data.basic-info.birth-city"
                          ns="components"
                        />
                      </Label>
                      <Controller
                        control={form.control}
                        name="birth_city"
                        render={({ field, fieldState }) => (
                          <Input
                            id="birth-city-input"
                            {...field}
                            placeholder={t('common.city-placeholder', {
                              ns: 'common',
                            })}
                            data-testid="birth-city-input"
                            required
                            error={!!fieldState.error}
                          />
                        )}
                      />
                    </fieldset>
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <fieldset className="col-span-1 grid gap-2">
                      <Label htmlFor="marital-status-selector" required>
                        <Translation i18nKey="common.marital-status" ns="common" />
                      </Label>
                      <Controller
                        control={form.control}
                        name="marital_status"
                        render={({ field, fieldState }) => (
                          <OptionsSelector
                            optionsAvailable={maritalStatusOptions}
                            onChangeValueSelected={field.onChange}
                            optionSelected={field.value}
                            defaultText={t('common.select-option', {
                              ns: 'common',
                            })}
                            selectType="white"
                            disabled={isUpdatingProfile}
                            dataTestId="marital-status-selector"
                            error={!!fieldState.error}
                            required
                          />
                        )}
                      />
                    </fieldset>

                    <fieldset className="col-span-1 grid gap-2">
                      <Label htmlFor="gender-selector" required>
                        <Translation i18nKey="common.gender" ns="common" />
                      </Label>
                      <Controller
                        control={form.control}
                        name="gender"
                        render={({ field, fieldState }) => (
                          <OptionsSelector
                            optionsAvailable={genderOptions}
                            onChangeValueSelected={field.onChange}
                            optionSelected={field.value}
                            defaultText={t('common.select-option', {
                              ns: 'common',
                            })}
                            selectType="white"
                            disabled={isUpdatingProfile}
                            dataTestId="gender-selector"
                            error={!!fieldState.error}
                            required
                          />
                        )}
                      />
                    </fieldset>
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <fieldset className="col-span-1 grid gap-2">
                      <Label htmlFor="nationality-selector" required>
                        <Translation
                          i18nKey="account-opening.personal-data.basic-info.nationality"
                          ns="components"
                        />
                      </Label>
                      <Controller
                        control={form.control}
                        name="nationality"
                        render={({ field, fieldState }) => (
                          <OptionsSelector
                            optionsAvailable={countryOptions}
                            onChangeValueSelected={field.onChange}
                            optionSelected={field.value}
                            defaultText={t(
                              'account-opening.personal-data.basic-info.nationality-placeholder',
                              {
                                ns: 'components',
                              },
                            )}
                            selectType="white"
                            disabled={isUpdatingProfile}
                            dataTestId="nationality-selector"
                            error={!!fieldState.error}
                            searchBar
                            required
                          />
                        )}
                      />
                    </fieldset>
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-12">
                    <fieldset className="col-span-1 grid content-start gap-2 lg:col-span-4">
                      <Label htmlFor="document-type-selector" required>
                        <Translation
                          i18nKey="account-opening.personal-data.basic-info.document-type"
                          ns="components"
                        />
                      </Label>
                      <Controller
                        control={form.control}
                        name="document_type"
                        render={({ field, fieldState }) => (
                          <OptionsSelector
                            optionsAvailable={documentTypeOptions}
                            onChangeValueSelected={field.onChange}
                            optionSelected={field.value}
                            defaultText={t(
                              'account-opening.personal-data.basic-info.document-type-placeholder',
                              {
                                ns: 'components',
                              },
                            )}
                            selectType="white"
                            disabled={isUpdatingProfile}
                            dataTestId="document-type-selector"
                            error={!!fieldState.error}
                            required
                          />
                        )}
                      />
                    </fieldset>

                    <fieldset className="col-span-1 grid content-start gap-2 lg:col-span-3">
                      <Label htmlFor="document-number-input" required>
                        <Translation
                          i18nKey="account-opening.personal-data.basic-info.document-number"
                          ns="components"
                        />
                      </Label>
                      <Controller
                        control={form.control}
                        name="document_number"
                        render={({ field, fieldState }) => (
                          <Input
                            id="document-number-input"
                            {...field}
                            placeholder={t(
                              'account-opening.personal-data.basic-info.document-number-placeholder',
                              {
                                ns: 'components',
                              },
                            )}
                            data-testid="document-number-input"
                            required
                            error={!!fieldState.error}
                          />
                        )}
                      />
                    </fieldset>

                    <fieldset className="col-span-1 grid content-start gap-2 lg:col-span-5">
                      <Label htmlFor="document-expiration-date-input" required>
                        <Translation
                          i18nKey="account-opening.personal-data.basic-info.document-expiration-date"
                          ns="components"
                        />
                      </Label>
                      <Controller
                        control={form.control}
                        name="document_expiration_date"
                        render={({ field, fieldState }) => (
                          <DateInput
                            id="document-expiration-date-input"
                            name={field.name}
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            ref={field.ref}
                            data-testid="document-expiration-date-input"
                            required={!hasPermanentValidity}
                            minDate={minDocumentExpirationDate}
                            error={!!fieldState.error}
                            disabled={hasPermanentValidity}
                          />
                        )}
                      />

                      {!hasPermanentValidity && (
                        <Typography
                          variant="body2"
                          className="!text-xs !text-subtitle"
                        >
                          <Translation
                            i18nKey="account-opening.personal-data.basic-info.document-expiration-min-validity-hint"
                            ns="components"
                          />
                        </Typography>
                      )}

                      <Controller
                        control={form.control}
                        name="document_permanent_validity"
                        render={({ field }) => (
                          <label className="inline-flex w-fit items-center gap-2 pt-1">
                            <Checkbox
                              checked={field.value}
                              onChange={(_, checked) => field.onChange(checked)}
                              className="!p-0"
                            />
                            <span className="text-sm text-subtitle">
                              <Translation
                                i18nKey="account-opening.personal-data.basic-info.document-permanent-validity"
                                ns="components"
                              />
                            </span>
                          </label>
                        )}
                      />
                    </fieldset>
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-12">
                    <fieldset className="col-span-1 grid gap-2 lg:col-span-6">
                      <Label htmlFor="email-input" required>
                        <Translation i18nKey="common.email" ns="common" />
                      </Label>
                      <Controller
                        control={form.control}
                        name="email"
                        render={({ field, fieldState }) => (
                          <Input
                            id="email-input"
                            type="email"
                            {...field}
                            placeholder={t('common.email-placeholder', {
                              ns: 'common',
                            })}
                            data-testid="email-input"
                            required
                            error={!!fieldState.error}
                            readOnly
                          />
                        )}
                      />
                    </fieldset>

                    <fieldset className="col-span-1 grid gap-2 lg:col-span-6">
                      <Label htmlFor="phone-input" required>
                        <Translation i18nKey="common.phone" ns="common" />
                      </Label>
                      <Controller
                        control={form.control}
                        name="phone_number"
                        render={({ field, fieldState }) => (
                          <Input
                            id="phone-input"
                            type="tel"
                            {...field}
                            placeholder={t('common.phone-placeholder', {
                              ns: 'common',
                            })}
                            data-testid="phone-input"
                            required
                            error={!!fieldState.error}
                          />
                        )}
                      />
                    </fieldset>
                  </div>
                </div>
              </Card>

              <Card
                className="!flex !flex-col !gap-2 !rounded-lg !p-6"
                variant="outlined"
              >
                <Typography
                  variant="h6"
                  className="!mb-6 !font-semibold"
                  data-testid="fiscal-address-section-title"
                >
                  <Translation
                    i18nKey="account-opening.personal-data.postal-address.title"
                    ns="components"
                  />
                </Typography>

                <div className="flex flex-col gap-5">
                  <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
                    <fieldset className="col-span-1 grid gap-2 xl:col-span-4">
                      <Label htmlFor="country-selector" required>
                        <Translation i18nKey="common.country" ns="common" />
                      </Label>
                      <Controller
                        control={form.control}
                        name="postal_address.country"
                        render={({ field, fieldState }) => (
                          <OptionsSelector
                            optionsAvailable={countryOptions}
                            onChangeValueSelected={field.onChange}
                            optionSelected={field.value}
                            defaultText={t('common.country-placeholder', {
                              ns: 'common',
                            })}
                            selectType="white"
                            disabled={isUpdatingProfile}
                            dataTestId="country-selector"
                            error={!!fieldState.error}
                            searchBar
                            required
                          />
                        )}
                      />
                    </fieldset>

                    {hasPostalProvinceOptions ? (
                      <>
                        <fieldset className="col-span-1 grid gap-2 xl:col-span-4">
                          <Label htmlFor="postal-province-selector" required>
                            <Translation
                              i18nKey="account-opening.personal-data.postal-address.province"
                              ns="components"
                            />
                          </Label>
                          <Controller
                            control={form.control}
                            name="postal_address.province"
                            render={({ field, fieldState }) => (
                              <OptionsSelector
                                optionsAvailable={postalProvinceOptions}
                                onChangeValueSelected={field.onChange}
                                optionSelected={field.value}
                                defaultText={t(
                                  'account-opening.personal-data.postal-address.province-placeholder',
                                  { ns: 'components' },
                                )}
                                selectType="white"
                                disabled={isUpdatingProfile}
                                dataTestId="postal-province-selector"
                                error={!!fieldState.error}
                                searchBar
                                required
                              />
                            )}
                          />
                        </fieldset>

                        <fieldset className="col-span-1 grid gap-2 xl:col-span-4">
                          <Label htmlFor="city-input" required>
                            <Translation i18nKey="common.city" ns="common" />
                          </Label>
                          <Controller
                            control={form.control}
                            name="postal_address.city"
                            render={({ field, fieldState }) => (
                              <Input
                                id="city-input"
                                {...field}
                                placeholder={t('common.city-placeholder', {
                                  ns: 'common',
                                })}
                                data-testid="city-input"
                                required
                                error={!!fieldState.error}
                              />
                            )}
                          />
                        </fieldset>
                      </>
                    ) : (
                      <fieldset className="col-span-1 grid gap-2 xl:col-span-8">
                        <Label htmlFor="city-input" required>
                          <Translation
                            i18nKey="account-opening.personal-data.postal-address.locality"
                            ns="components"
                          />
                        </Label>
                        <Controller
                          control={form.control}
                          name="postal_address.city"
                          render={({ field, fieldState }) => (
                            <Input
                              id="city-input"
                              {...field}
                              placeholder={t(
                                'account-opening.personal-data.postal-address.locality-placeholder',
                                { ns: 'components' },
                              )}
                              data-testid="city-input"
                              required
                              error={!!fieldState.error}
                            />
                          )}
                        />
                      </fieldset>
                    )}
                  </div>

                  <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
                    {hasPostalProvinceOptions && (
                      <fieldset className="col-span-1 grid gap-2 xl:col-span-4">
                        <Label htmlFor="postal-street-type-selector" required>
                          <Translation
                            i18nKey="account-opening.personal-data.postal-address.street-type"
                            ns="components"
                          />
                        </Label>
                        <Controller
                          control={form.control}
                          name="postal_address.street_type"
                          render={({ field, fieldState }) => (
                            <OptionsSelector
                              optionsAvailable={streetTypeOptions}
                              onChangeValueSelected={field.onChange}
                              optionSelected={field.value}
                              defaultText={t(
                                'account-opening.personal-data.postal-address.street-type-placeholder',
                                { ns: 'components' },
                              )}
                              selectType="white"
                              disabled={isUpdatingProfile}
                              dataTestId="postal-street-type-selector"
                              error={!!fieldState.error}
                              searchBar
                              required
                            />
                          )}
                        />
                      </fieldset>
                    )}

                    <fieldset
                      className={`col-span-1 grid gap-2 ${
                        hasPostalProvinceOptions
                          ? 'xl:col-span-8'
                          : 'xl:col-span-12'
                      }`}
                    >
                      <Label htmlFor="postal-street-name-input" required>
                        <Translation
                          i18nKey="account-opening.personal-data.postal-address.street-name"
                          ns="components"
                        />
                      </Label>
                      <Controller
                        control={form.control}
                        name="postal_address.street_name"
                        render={({ field, fieldState }) => (
                          <Input
                            id="postal-street-name-input"
                            {...field}
                            placeholder={t(
                              'account-opening.personal-data.postal-address.street-name-placeholder',
                              { ns: 'components' },
                            )}
                            data-testid="postal-street-name-input"
                            required
                            error={!!fieldState.error}
                          />
                        )}
                      />
                    </fieldset>
                  </div>

                  <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
                    <fieldset className="col-span-1 grid gap-2 xl:col-span-3">
                      <Label htmlFor="postal-street-number-input" required>
                        <Translation
                          i18nKey="account-opening.personal-data.postal-address.street-number"
                          ns="components"
                        />
                      </Label>
                      <Controller
                        control={form.control}
                        name="postal_address.street_number"
                        render={({ field, fieldState }) => (
                          <Input
                            id="postal-street-number-input"
                            {...field}
                            placeholder={t(
                              'account-opening.personal-data.postal-address.street-number-placeholder',
                              { ns: 'components' },
                            )}
                            data-testid="postal-street-number-input"
                            required
                            error={!!fieldState.error}
                          />
                        )}
                      />
                    </fieldset>

                    <fieldset className="col-span-1 grid gap-2 xl:col-span-3">
                      <Label htmlFor="postal-code-input" required>
                        <Translation i18nKey="common.postal-code" ns="common" />
                      </Label>
                      <Controller
                        control={form.control}
                        name="postal_address.postal_code"
                        render={({ field, fieldState }) => (
                          <Input
                            id="postal-code-input"
                            {...field}
                            placeholder={t('common.postal-code-placeholder', {
                              ns: 'common',
                            })}
                            data-testid="postal-code-input"
                            required
                            error={!!fieldState.error}
                          />
                        )}
                      />
                    </fieldset>

                    <fieldset className="col-span-1 grid gap-2 xl:col-span-6">
                      <Label htmlFor="postal-additional-address-input">
                        <Translation
                          i18nKey="account-opening.personal-data.postal-address.additional-address-info"
                          ns="components"
                        />
                      </Label>
                      <Controller
                        control={form.control}
                        name="postal_address.additional_address_info"
                        render={({ field, fieldState }) => (
                          <Input
                            id="postal-additional-address-input"
                            {...field}
                            placeholder={t(
                              'account-opening.personal-data.postal-address.additional-address-info-placeholder',
                              { ns: 'components' },
                            )}
                            data-testid="postal-additional-address-input"
                            error={!!fieldState.error}
                          />
                        )}
                      />
                    </fieldset>
                  </div>

                  <Controller
                    control={form.control}
                    name="has_different_fiscal_address"
                    render={({ field }) => (
                      <label className="flex items-center gap-2 pt-1">
                        <Checkbox
                          checked={field.value}
                          onChange={(_, checked) => field.onChange(checked)}
                          className="!p-0"
                        />
                        <span className="text-sm font-medium">
                          <Translation
                            i18nKey="account-opening.personal-data.postal-address.different-fiscal-address"
                            ns="components"
                          />
                        </span>
                      </label>
                    )}
                  />

                  {hasDifferentFiscalAddress && (
                    <>
                      <Divider />

                      <Typography
                        variant="body1"
                        className="!mb-1 !text-base !font-semibold !text-primary-medium"
                        data-testid="fiscal-address-title"
                      >
                        <Translation
                          i18nKey="account-opening.personal-data.fiscal-address.title"
                          ns="components"
                        />
                      </Typography>

                      <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
                        <fieldset className="col-span-1 grid gap-2 xl:col-span-4">
                          <Label htmlFor="fiscal-country-selector" required>
                            <Translation i18nKey="common.country" ns="common" />
                          </Label>
                          <Controller
                            control={form.control}
                            name="fiscal_address.country"
                            render={({ field, fieldState }) => (
                              <OptionsSelector
                                optionsAvailable={countryOptions}
                                onChangeValueSelected={field.onChange}
                                optionSelected={field.value}
                                defaultText={t('common.country-placeholder', {
                                  ns: 'common',
                                })}
                                selectType="white"
                                disabled={isUpdatingProfile}
                                dataTestId="fiscal-country-selector"
                                error={!!fieldState.error}
                                searchBar
                                required
                              />
                            )}
                          />
                        </fieldset>

                        {hasFiscalProvinceOptions ? (
                          <>
                            <fieldset className="col-span-1 grid gap-2 xl:col-span-4">
                              <Label htmlFor="fiscal-province-selector" required>
                                <Translation
                                  i18nKey="account-opening.personal-data.fiscal-address.province"
                                  ns="components"
                                />
                              </Label>
                              <Controller
                                control={form.control}
                                name="fiscal_address.province"
                                render={({ field, fieldState }) => (
                                  <OptionsSelector
                                    optionsAvailable={fiscalProvinceOptions}
                                    onChangeValueSelected={field.onChange}
                                    optionSelected={field.value}
                                    defaultText={t(
                                      'account-opening.personal-data.fiscal-address.province-placeholder',
                                      { ns: 'components' },
                                    )}
                                    selectType="white"
                                    disabled={isUpdatingProfile}
                                    dataTestId="fiscal-province-selector"
                                    error={!!fieldState.error}
                                    searchBar
                                    required
                                  />
                                )}
                              />
                            </fieldset>

                            <fieldset className="col-span-1 grid gap-2 xl:col-span-4">
                              <Label htmlFor="fiscal-city-input" required>
                                <Translation i18nKey="common.city" ns="common" />
                              </Label>
                              <Controller
                                control={form.control}
                                name="fiscal_address.city"
                                render={({ field, fieldState }) => (
                                  <Input
                                    id="fiscal-city-input"
                                    {...field}
                                    placeholder={t('common.city-placeholder', {
                                      ns: 'common',
                                    })}
                                    data-testid="fiscal-city-input"
                                    required
                                    error={!!fieldState.error}
                                  />
                                )}
                              />
                            </fieldset>
                          </>
                        ) : (
                          <fieldset className="col-span-1 grid gap-2 xl:col-span-8">
                            <Label htmlFor="fiscal-city-input" required>
                              <Translation
                                i18nKey="account-opening.personal-data.fiscal-address.locality"
                                ns="components"
                              />
                            </Label>
                            <Controller
                              control={form.control}
                              name="fiscal_address.city"
                              render={({ field, fieldState }) => (
                                <Input
                                  id="fiscal-city-input"
                                  {...field}
                                  placeholder={t(
                                    'account-opening.personal-data.fiscal-address.locality-placeholder',
                                    { ns: 'components' },
                                  )}
                                  data-testid="fiscal-city-input"
                                  required
                                  error={!!fieldState.error}
                                />
                              )}
                            />
                          </fieldset>
                        )}
                      </div>

                      <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
                        {hasFiscalProvinceOptions && (
                          <fieldset className="col-span-1 grid gap-2 xl:col-span-4">
                            <Label htmlFor="fiscal-street-type-selector" required>
                              <Translation
                                i18nKey="account-opening.personal-data.fiscal-address.street-type"
                                ns="components"
                              />
                            </Label>
                            <Controller
                              control={form.control}
                              name="fiscal_address.street_type"
                              render={({ field, fieldState }) => (
                                <OptionsSelector
                                  optionsAvailable={streetTypeOptions}
                                  onChangeValueSelected={field.onChange}
                                  optionSelected={field.value}
                                  defaultText={t(
                                    'account-opening.personal-data.fiscal-address.street-type-placeholder',
                                    { ns: 'components' },
                                  )}
                                  selectType="white"
                                  disabled={isUpdatingProfile}
                                  dataTestId="fiscal-street-type-selector"
                                  error={!!fieldState.error}
                                  searchBar
                                  required
                                />
                              )}
                            />
                          </fieldset>
                        )}

                        <fieldset
                          className={`col-span-1 grid gap-2 ${
                            hasFiscalProvinceOptions
                              ? 'xl:col-span-8'
                              : 'xl:col-span-12'
                          }`}
                        >
                          <Label htmlFor="fiscal-street-name-input" required>
                            <Translation
                              i18nKey="account-opening.personal-data.fiscal-address.street-name"
                              ns="components"
                            />
                          </Label>
                          <Controller
                            control={form.control}
                            name="fiscal_address.street_name"
                            render={({ field, fieldState }) => (
                              <Input
                                id="fiscal-street-name-input"
                                {...field}
                                placeholder={t(
                                  'account-opening.personal-data.fiscal-address.street-name-placeholder',
                                  { ns: 'components' },
                                )}
                                data-testid="fiscal-street-name-input"
                                required
                                error={!!fieldState.error}
                              />
                            )}
                          />
                        </fieldset>
                      </div>

                      <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
                        <fieldset className="col-span-1 grid gap-2 xl:col-span-3">
                          <Label htmlFor="fiscal-street-number-input" required>
                            <Translation
                              i18nKey="account-opening.personal-data.fiscal-address.street-number"
                              ns="components"
                            />
                          </Label>
                          <Controller
                            control={form.control}
                            name="fiscal_address.street_number"
                            render={({ field, fieldState }) => (
                              <Input
                                id="fiscal-street-number-input"
                                {...field}
                                placeholder={t(
                                  'account-opening.personal-data.fiscal-address.street-number-placeholder',
                                  { ns: 'components' },
                                )}
                                data-testid="fiscal-street-number-input"
                                required
                                error={!!fieldState.error}
                              />
                            )}
                          />
                        </fieldset>

                        <fieldset className="col-span-1 grid gap-2 xl:col-span-3">
                          <Label htmlFor="fiscal-postal-code-input" required>
                            <Translation i18nKey="common.postal-code" ns="common" />
                          </Label>
                          <Controller
                            control={form.control}
                            name="fiscal_address.postal_code"
                            render={({ field, fieldState }) => (
                              <Input
                                id="fiscal-postal-code-input"
                                {...field}
                                placeholder={t('common.postal-code-placeholder', {
                                  ns: 'common',
                                })}
                                data-testid="fiscal-postal-code-input"
                                required
                                error={!!fieldState.error}
                              />
                            )}
                          />
                        </fieldset>

                        <fieldset className="col-span-1 grid gap-2 xl:col-span-6">
                          <Label htmlFor="fiscal-additional-address-input">
                            <Translation
                              i18nKey="account-opening.personal-data.fiscal-address.additional-address-info"
                              ns="components"
                            />
                          </Label>
                          <Controller
                            control={form.control}
                            name="fiscal_address.additional_address_info"
                            render={({ field, fieldState }) => (
                              <Input
                                id="fiscal-additional-address-input"
                                {...field}
                                placeholder={t(
                                  'account-opening.personal-data.fiscal-address.additional-address-info-placeholder',
                                  { ns: 'components' },
                                )}
                                data-testid="fiscal-additional-address-input"
                                error={!!fieldState.error}
                              />
                            )}
                          />
                        </fieldset>
                      </div>
                    </>
                  )}
                </div>
              </Card>
            </fieldset>
          </form>
        </div>
      </ScreenLayout>
    );
  },
});
